import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { publicAPI } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import SearchBar from '../../components/SearchBar'
import ExportButtons from '../../components/ExportButtons'
import { ColumnDefinition } from '../../utils/exportToCsv'
import { formatNorwegianDate } from '../../utils/dateUtils'
import { useTheme } from '../../contexts/ThemeContext'
import { localizeApiDetail } from '../../i18n/apiError'
import { useLatest } from '../Bruktbil/useLatest'
import {
  TABLE_CATEGORIES, categoryCount, changeColor, fuelColor, int, pct, shareChangePp, shareOf, signed,
  visibleTops, withEkskluder,
} from './drivstoff'
import type { AkseldriftQuery, DrivstoffMerker, DrivstoffModeller, FuelCategory, FuelColumns, Periods } from './types'

type Row = FuelColumns & { brand: string; model?: string; master_model_id?: number }
type ValueKey = 'total' | 'count' | 'share' | 'shareChange' | 'totalChange'
type SortKey = 'brand' | 'model' | ValueKey
type Sort = { key: SortKey; direction: 'asc' | 'desc' } | null

// Vertical line between column groups, same as Nybilstatistikk
const DIVIDER = 'border-r-2 border-gray-300 dark:border-gray-600'

/** The three category columns for one row, derived from its leaf counts */
function categoryValues(r: FuelColumns, kategori: FuelCategory): Record<ValueKey, number | null> {
  const count = categoryCount(r.counts, kategori)
  const previous = r.previous_counts ? categoryCount(r.previous_counts, kategori) : null
  return {
    total: r.total,
    count,
    share: shareOf(count, r.total),
    shareChange: shareChangePp(count, r.total, previous, r.previous_total),
    totalChange: r.total_change_pct,
  }
}

/**
 * Teknisk › Drivstoff › Merke and › Modell: one row per brand or model. «Vis kategori»
 * picks which category the count, share and share-change columns show, without a new
 * API call (AC9); «Fordeling» shows every category of the row as one small bar.
 */
export default function DrivstoffTable({ kind, queryKey, ekskluderElbil, sortKategori, onPeriods }: {
  kind: 'merke' | 'modell'
  queryKey: string
  ekskluderElbil: boolean
  /** Set by «Se alle» on Oversikt: start on this category, sorted by its count */
  sortKategori: FuelCategory | null
  /** Reports the periods of the latest answer, for the explanation box */
  onPeriods: (periods: Periods) => void
}) {
  const { t } = useTranslation('teknisk')
  const { isDark } = useTheme()
  const [response, setResponse] = useState<DrivstoffMerker | DrivstoffModeller | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [choice, setChoice] = useState<FuelCategory>(sortKategori ?? 'elbil')
  const [sort, setSort] = useState<Sort>(sortKategori ? { key: 'count', direction: 'desc' } : null)
  const isModel = kind === 'modell'
  const kategori: FuelCategory = ekskluderElbil && choice === 'elbil' ? 'hybrid' : choice
  const tops = visibleTops(ekskluderElbil)
  const categories = ekskluderElbil ? TABLE_CATEGORIES.filter(k => k !== 'elbil') : TABLE_CATEGORIES

  useLatest(alive => {
    const params = withEkskluder(JSON.parse(queryKey) as AkseldriftQuery, ekskluderElbil)
    setLoading(true)
    setError(null)
    const request = isModel ? publicAPI.getDrivstoffModeller(params) : publicAPI.getDrivstoffMerker(params)
    request
      .then(res => {
        if (!alive()) return
        setResponse(res.data)
        onPeriods(res.data.periods)
      })
      .catch(err => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('drivstoff.loadError'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [queryKey, isModel, ekskluderElbil])

  const rows = useMemo(() => {
    const data: Row[] = response?.data ?? []
    const needle = search.trim().toLowerCase()
    const filtered = needle
      ? data.filter(r => `${r.brand} ${r.model ?? ''}`.toLowerCase().includes(needle))
      : data
    if (!sort) return filtered
    const dir = sort.direction === 'asc' ? 1 : -1
    if (sort.key === 'brand' || sort.key === 'model') {
      const key = sort.key
      return [...filtered].sort((a, b) => dir * String(a[key] ?? '').localeCompare(String(b[key] ?? ''), 'nb'))
    }
    const key = sort.key
    return [...filtered].sort((a, b) => {
      // Rows without a value (no comparison) always go last
      const av = categoryValues(a, kategori)[key]
      const bv = categoryValues(b, kategori)[key]
      if (av === null) return bv === null ? 0 : 1
      if (bv === null) return -1
      return dir * (av - bv)
    })
  }, [response, search, sort, kategori])

  const handleSort = (key: SortKey) => {
    if (sort?.key === key) setSort(sort.direction === 'asc' ? { key, direction: 'desc' } : null)
    else setSort({ key, direction: 'asc' })
  }

  const sortIcon = (key: SortKey) =>
    sort?.key === key
      ? sort.direction === 'asc' ? <ArrowUp className="w-4 h-4 inline ml-1" /> : <ArrowDown className="w-4 h-4 inline ml-1" />
      : <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />

  // The hybrid branches read «Ladbar hybrid» on their own
  const label = (k: FuelCategory) =>
    k === 'ladbar' || k === 'ikke_ladbar' ? t(`drivstoff.categoryLong.${k}` as const) : t(`drivstoff.category.${k}` as const)
  const categoryLabel = label(kategori)

  // `divider` closes a column group with a vertical line, as on Nybilstatistikk
  const columns: { key: ValueKey; label: string; render: (v: Record<ValueKey, number | null>) => React.ReactNode; divider?: boolean }[] = [
    { key: 'total', label: t('drivstoff.table.total'), render: v => int(v.total ?? 0), divider: true },
    { key: 'count', label: t('drivstoff.table.count', { category: categoryLabel }), render: v => int(v.count ?? 0) },
    { key: 'share', label: t('drivstoff.table.share', { category: categoryLabel }), render: v => pct(v.share) },
    {
      key: 'shareChange', label: t('drivstoff.table.shareChange'), divider: true,
      render: v => <span className={changeColor(v.shareChange)}>{signed(v.shareChange)}</span>,
    },
    {
      key: 'totalChange', label: t('drivstoff.table.totalChange'), divider: true,
      render: v => <span className={changeColor(v.totalChange)}>{signed(v.totalChange)}</span>,
    },
  ]

  const headerLabel = (key: SortKey) =>
    key === 'brand' ? t('drivstoff.colBrand') : key === 'model' ? t('drivstoff.colModel')
      : columns.find(c => c.key === key)?.label ?? ''

  const mixBar = (r: FuelColumns) => {
    const parts = tops
      .map(k => ({ k, count: categoryCount(r.counts, k) }))
      .filter(p => p.count > 0)
    const text = parts.map(p => `${label(p.k)} ${pct(shareOf(p.count, r.total))}`).join(', ')
    return (
      <div
        className="flex h-3 w-32 rounded-sm overflow-hidden bg-gray-100 dark:bg-gray-800"
        role="img"
        aria-label={t('drivstoff.table.mixLabel', { value: text })}
        title={text}
      >
        {parts.map(p => (
          <span key={p.k} style={{ width: `${(p.count * 100) / r.total}%`, backgroundColor: fuelColor(p.k, isDark) }} />
        ))}
      </div>
    )
  }

  const query: AkseldriftQuery = JSON.parse(queryKey)
  // The export has one count column per category instead of the picked one (AC9)
  const exportColumns: ColumnDefinition[] = [
    { key: 'rank', label: '#' },
    { key: 'brand', label: t('drivstoff.colBrand') },
    ...(isModel ? [{ key: 'model', label: t('drivstoff.colModel') }] : []),
    { key: 'total', label: t('drivstoff.table.total') },
    ...categories.map(k => ({ key: `count_${k}`, label: t('drivstoff.table.count', { category: label(k) }) })),
    { key: 'total_change_pct', label: t('drivstoff.table.totalChange'), format: (v: number | null) => signed(v) },
  ]
  const exportRows = rows.map((r, i) => ({
    rank: i + 1,
    brand: r.brand,
    model: r.model,
    total: r.total,
    ...Object.fromEntries(categories.map(k => [`count_${k}`, categoryCount(r.counts, k)])),
    total_change_pct: r.total_change_pct,
  }))

  const title = isModel ? t('drivstoff.table.titleModels') : t('drivstoff.table.titleBrands')
  const summary = response?.summary

  return (
    <div className="card p-6" data-testid={`drivstoff-table-${kind}`}>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {title}
          {sort && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
              {t('drivstoff.table.sortedBy', { column: headerLabel(sort.key) })}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {sort && (
            <button onClick={() => setSort(null)} className="btn-secondary text-sm">
              {t('drivstoff.table.resetSort')}
            </button>
          )}
          <ExportButtons
            data={exportRows}
            columns={exportColumns}
            metadata={{
              title: `${t('title')} – ${t('tabs.drivstoff')} – ${title}`,
              dateRange: response
                ? `${formatNorwegianDate(response.periods.current.start_date)} – ${formatNorwegianDate(response.periods.current.end_date)}`
                : '',
              filters: [
                query.varebil === true ? t('drivstoff.table.filterVarebil') : query.varebil === false ? t('drivstoff.table.filterPersonbil') : '',
                query.mine_postnummer ? t('drivstoff.table.filterPostnummer', { value: query.mine_postnummer }) : '',
                ekskluderElbil ? t('drivstoff.table.filterExcluded') : '',
              ].filter(Boolean),
            }}
            disabled={loading}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex-1 min-w-[240px]">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={isModel ? t('drivstoff.table.searchModels') : t('drivstoff.table.searchBrands')}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" role="group" data-testid="drivstoff-vis-kategori">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('drivstoff.table.showCategory')}</span>
        {TABLE_CATEGORIES.map(k => {
          const disabled = ekskluderElbil && k === 'elbil'
          return (
            <button
              key={k}
              onClick={() => setChoice(k)}
              disabled={disabled}
              aria-pressed={kategori === k}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                disabled
                  ? 'bg-gray-100 dark:bg-dark-card text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : kategori === k
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
              }`}
            >
              {label(k)}
            </button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-2 text-xs text-gray-600 dark:text-gray-400">
        <span>{t('drivstoff.table.mixLegend')}</span>
        {tops.map(k => (
          <span key={k} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: fuelColor(k, isDark) }} aria-hidden="true" />
            {label(k)}
          </span>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-center w-12">#</th>
                <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand')}>
                  {t('drivstoff.colBrand')} {sortIcon('brand')}
                </th>
                {isModel && (
                  <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('model')}>
                    {t('drivstoff.colModel')} {sortIcon('model')}
                  </th>
                )}
                {columns.map(c => (
                  <th key={c.key} className={`text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${c.divider ? DIVIDER : ''}`} onClick={() => handleSort(c.key)}>
                    {c.label} {sortIcon(c.key)}
                  </th>
                ))}
                <th className="text-left">{t('drivstoff.table.mix')}</th>
              </tr>
            </thead>
            <tbody>
              {summary && (
                <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                  <td className="text-center py-2"><span className="text-primary-600 dark:text-primary-400">&#8721;</span></td>
                  <td className="font-bold text-primary-700 dark:text-primary-300 py-2" colSpan={isModel ? 2 : 1}>TOTAL</td>
                  {columns.map(c => (
                    <td key={c.key} className={`text-right py-2 ${c.divider ? DIVIDER : ''}`}>{c.render(categoryValues(summary, kategori))}</td>
                  ))}
                  <td className="py-2">{summary.total > 0 && mixBar(summary)}</td>
                </tr>
              )}
              {rows.map((r, i) => {
                const values = categoryValues(r, kategori)
                return (
                  <tr key={isModel ? r.master_model_id : r.brand}>
                    <td className="text-center text-gray-500 dark:text-gray-400">{i + 1}</td>
                    <td>{r.brand}</td>
                    {isModel && <td>{r.model}</td>}
                    {columns.map(c => <td key={c.key} className={`text-right ${c.divider ? DIVIDER : ''}`}>{c.render(values)}</td>)}
                    <td>{r.total > 0 && mixBar(r)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {rows.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">{t('drivstoff.noData')}</p>
          )}
        </div>
      )}
    </div>
  )
}
